/**
 * Accueil SmartLawyer Desktop : recherche juridique + agents (sans marketing).
 * Route : / en mode desktop (cf. isDesktop() dans AppMode.ts)
 */
import { desktopBridge } from '@/AppMode';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { PublicHeader } from '@/components/jurisprudence';
import { FicheAgentChat } from '@/components/jurisprudence/FicheAgentChat';

interface Agent {
  slug: string;
  name: string;
  image?: string | null;
  icon_emoji?: string | null;
  domaines: string[];
}

const SHORTCUTS = [
  { to: '/articles', label: 'Codes & lois', icon: '📘' },
  { to: '/jurisprudence/cjue', label: 'CJUE', icon: '🇪🇺' },
  {
    to: '/conseil-constitutionnel',
    label: 'Conseil constitutionnel',
    icon: '🏛️'
  },
  { to: '/cnil/deliberations', label: 'CNIL', icon: '🔐' },
  { to: '/legal-graph', label: 'Legal Graph', icon: '🕸️' },
  { to: '/tribunal', label: 'Tribunal', icon: '🔨' }
];

function AgentAvatar({ agent, size = 64 }: { agent: Agent; size?: number }) {
  const [broken, setBroken] = useState(false);
  return agent.image && !broken ? (
    <img
      src={agent.image}
      alt={agent.name}
      width={size}
      height={size}
      onError={() => setBroken(true)}
      className="rounded-full object-cover bg-violet-50 dark:bg-violet-950/40"
      style={{ width: size, height: size }}
    />
  ) : (
    <div
      className="rounded-full bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center"
      style={{ width: size, height: size, fontSize: size / 2 }}
    >
      {agent.icon_emoji || '⚖️'}
    </div>
  );
}

export default function DesktopHome() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [agents, setAgents] = useState<Agent[]>([]);
  const [active, setActive] = useState<Agent | null>(null);
  const bridge = desktopBridge();

  useEffect(() => {
    fetch('/api/fiches-agent/agents')
      .then((r) => (r.ok ? r.json() : { agents: [] }))
      .then((d) => setAgents(d.agents ?? []))
      .catch(() => setAgents([]));
  }, []);

  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setActive(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-950">
      <PublicHeader />
      <main className="flex-1 w-full max-w-5xl mx-auto px-4 py-10">
        {/* Recherche */}
        <section className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {t('desktop.home.title')}
          </h1>
          <p className="mt-1 text-gray-500 dark:text-gray-400">
            {t('desktop.home.subtitle')}
          </p>
          <form
            className="mt-6 flex gap-2 max-w-3xl mx-auto"
            onSubmit={(e) => {
              e.preventDefault();
              if (query.trim())
                navigate(`/?q=${encodeURIComponent(query.trim())}`);
            }}
          >
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('desktop.home.search')}
              className="flex-1 text-base border border-gray-200 dark:border-gray-700 rounded-2xl px-5 py-4 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
            />
            <button
              type="submit"
              aria-label={t('desktop.home.search')}
              className="px-6 rounded-2xl bg-violet-600 hover:bg-violet-700 text-white"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="7" />
                <path strokeLinecap="round" d="M20 20l-3.5-3.5" />
              </svg>
            </button>
          </form>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {SHORTCUTS.map((s) => (
              <Link
                key={s.to}
                to={s.to}
                className="text-sm px-3 py-1.5 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:border-violet-400"
              >
                {s.icon} {s.label}
              </Link>
            ))}
            {bridge && (
              <button
                onClick={() => bridge.app.openLocal?.('dossiers')}
                className="text-sm px-3 py-1.5 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:border-violet-400"
              >
                📁 {t('desktop.home.my_files')}
              </button>
            )}
          </div>
        </section>

        {/* Agents */}
        <section className="mt-14">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {t('desktop.home.agents_title')}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('desktop.home.agents_subtitle')}
          </p>
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {agents.map((a) => (
              <button
                key={a.slug}
                onClick={() => setActive(a)}
                className="group flex flex-col items-center text-center p-4 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 hover:border-violet-400 hover:shadow-md transition-all"
              >
                <AgentAvatar agent={a} />
                <span className="mt-3 font-semibold text-gray-900 dark:text-gray-100">
                  {a.name}
                </span>
                <span className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 capitalize">
                  {a.domaines.join(' · ')}
                </span>
              </button>
            ))}
          </div>
        </section>
      </main>

      {/* Conversation avec l'agent (même widget que sur les fiches) */}
      {active && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-40"
            onClick={() => setActive(null)}
          />
          <aside className="fixed top-0 right-0 h-full w-full max-w-xl z-50 bg-white dark:bg-gray-950 border-l border-gray-200 dark:border-gray-800 flex flex-col shadow-2xl">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-800">
              <AgentAvatar agent={active} size={36} />
              <div className="flex-1">
                <div className="font-semibold text-gray-900 dark:text-gray-100">
                  {active.name}
                </div>
                <div className="text-xs text-gray-500 capitalize">
                  {active.domaines.join(' · ')}
                </div>
              </div>
              <button
                onClick={() => setActive(null)}
                className="text-sm text-gray-500 hover:text-gray-900 dark:hover:text-gray-100"
              >
                {t('desktop.home.close')}
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <FicheAgentChat
                key={active.slug}
                ficheId=""
                agentSlug={active.slug}
                ficheTitle={active.name}
              />
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
